import { gql, type TypedDocumentNode } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { type GQL } from "@binale-tech/shared";

// Documents are written WITHOUT template interpolation so that scripts/validate-graphql.mjs
// (which skips any tagged template containing an interpolation) actually validates them.
export const mailboxConfigQuery: TypedDocumentNode<
    Pick<GQL.IQuery, "mailboxConfig">,
    GQL.IQueryMailboxConfigArgs
> = gql`
    query mailboxConfig($companyId: ID!) {
        mailboxConfig(companyId: $companyId) {
            companyId
            provider
            host
            port
            secure
            username
            folder
            hasPassword
            enabled
            connectionStatus
            lastPolledAt
            lastError
        }
    }
`;

const mailboxConfigSaveMutation: TypedDocumentNode<
    Pick<GQL.IMutation, "mailboxConfigSave">,
    GQL.IMutationMailboxConfigSaveArgs
> = gql`
    mutation mailboxConfigSave($input: MailboxConfigSaveInput!) {
        mailboxConfigSave(input: $input) {
            companyId
            provider
            host
            port
            secure
            username
            folder
            hasPassword
            enabled
            connectionStatus
            lastPolledAt
            lastError
        }
    }
`;

const mailboxConfigDeleteMutation: TypedDocumentNode<
    Pick<GQL.IMutation, "mailboxConfigDelete">,
    GQL.IMutationMailboxConfigDeleteArgs
> = gql`
    mutation mailboxConfigDelete($companyId: ID!) {
        mailboxConfigDelete(companyId: $companyId)
    }
`;

const mailboxConfigTestConnectionMutation: TypedDocumentNode<
    Pick<GQL.IMutation, "mailboxConfigTestConnection">,
    GQL.IMutationMailboxConfigTestConnectionArgs
> = gql`
    mutation mailboxConfigTestConnection($input: MailboxConfigTestConnectionInput!) {
        mailboxConfigTestConnection(input: $input) {
            ok
            error
        }
    }
`;

const mailboxOAuthObtainAuthUrlMutation: TypedDocumentNode<
    Pick<GQL.IMutation, "mailboxOAuthObtainAuthUrl">,
    GQL.IMutationMailboxOAuthObtainAuthUrlArgs
> = gql`
    mutation mailboxOAuthObtainAuthUrl($input: MailboxOAuthObtainAuthUrlInput!) {
        mailboxOAuthObtainAuthUrl(input: $input) {
            url
        }
    }
`;

const mailboxOAuthTokenGrantMutation: TypedDocumentNode<
    Pick<GQL.IMutation, "mailboxOAuthTokenGrant">,
    GQL.IMutationMailboxOAuthTokenGrantArgs
> = gql`
    mutation mailboxOAuthTokenGrant($input: MailboxOAuthTokenGrantInput!) {
        mailboxOAuthTokenGrant(input: $input) {
            companyId
            provider
            host
            port
            secure
            username
            folder
            hasPassword
            enabled
            connectionStatus
            lastPolledAt
            lastError
        }
    }
`;

/** localStorage correlation between the connect click and the /m365/callback page (mirrors DATEV's key). */
export const MAILBOX_OAUTH_LS_KEY = "mailboxOAuth";
export interface MailboxOAuthLSData {
    companyId: string;
}

export class MailboxGraphql {
    static readonly mailboxConfigQuery = mailboxConfigQuery;
    static readonly mailboxConfigSaveMutation = mailboxConfigSaveMutation;
    static readonly mailboxConfigDeleteMutation = mailboxConfigDeleteMutation;
    static readonly mailboxConfigTestConnectionMutation = mailboxConfigTestConnectionMutation;
}

export const useMailboxConfig = (companyId: string, skip: boolean) =>
    useQuery(mailboxConfigQuery, {
        variables: { companyId },
        skip: skip || !companyId,
        fetchPolicy: "no-cache",
    });

export const useMailboxConfigSave = () => useMutation(mailboxConfigSaveMutation);

export const useMailboxConfigDelete = () => useMutation(mailboxConfigDeleteMutation);

export const useMailboxConfigTestConnection = () => useMutation(mailboxConfigTestConnectionMutation);

export const useMailboxOAuthObtainAuthUrl = () => useMutation(mailboxOAuthObtainAuthUrlMutation);

export const useMailboxOAuthTokenGrant = () => useMutation(mailboxOAuthTokenGrantMutation);
